import {
  Dispatch,
  SetStateAction,
  memo,
  useEffect,
  useRef,
  useState,
} from 'react';

import { Drawer } from 'vaul';

type DrawerStep = 'phone' | 'otp';

type Props = {
  open: boolean;
  setOpen: Dispatch<SetStateAction<boolean>>;
  step: DrawerStep;
  onSubmitPhone: (mobile: string) => void;
  onSubmitOtp: (otp: string) => void;
  onResend: () => void;
  onBack: () => void;
  isLoading: boolean;
  error: string | null;
  mobile?: string;
};

type Country = { label: string; code: string };

// Dialing codes served by this portal, matching the SG deployment's
// LOGIN_COUNTRY_OPTIONS. Typed as a fixed-length tuple (rather than
// Country[]) so COUNTRIES[0] is known non-undefined under this repo's
// noUncheckedIndexedAccess tsconfig option.
const COUNTRIES: [Country, Country, Country] = [
  { label: 'Singapore +65', code: '65' },
  { label: 'Hong Kong +852', code: '852' },
  { label: 'Mainland China +86', code: '86' },
];

const OTP_LENGTH = 4;
const RESEND_SECONDS = 60;

const LoginDrawer = ({
  open,
  setOpen,
  step,
  onSubmitPhone,
  onSubmitOtp,
  onResend,
  onBack,
  isLoading,
  error,
  mobile,
}: Props) => {
  const [countryCode, setCountryCode] = useState(COUNTRIES[0].code);
  const [nationalNumber, setNationalNumber] = useState('');
  const [otp, setOtp] = useState('');
  const [cooldown, setCooldown] = useState(0);
  const lastSubmitted = useRef('');

  // Start the resend cooldown whenever the OTP step is entered.
  useEffect(() => {
    if (step === 'otp') {
      setOtp('');
      setCooldown(RESEND_SECONDS);
      lastSubmitted.current = '';
    }
  }, [step]);

  useEffect(() => {
    if (cooldown <= 0) return undefined;
    const timer = setTimeout(() => setCooldown((s) => s - 1), 1000);
    return () => clearTimeout(timer);
  }, [cooldown]);

  // Clear the field when a verification error comes back, so a wrong code
  // is never left sitting in the input, and reset the guard below so the
  // user can retype (and resubmit) the same digits.
  useEffect(() => {
    if (error) {
      setOtp('');
      lastSubmitted.current = '';
    }
  }, [error]);

  const handlePhoneSubmit = () => {
    const digits = nationalNumber.replace(/\D/g, '');
    if (!digits) return;
    onSubmitPhone(`${countryCode}${digits}`);
  };

  const handleOtpChange = (value: string) => {
    const digits = value.replace(/\D/g, '').slice(0, OTP_LENGTH);
    setOtp(digits);
    if (digits.length === OTP_LENGTH && digits !== lastSubmitted.current) {
      lastSubmitted.current = digits;
      onSubmitOtp(digits);
    }
  };

  const handleResend = () => {
    if (cooldown > 0) return;
    setCooldown(RESEND_SECONDS);
    onResend();
  };

  return (
    <Drawer.Root open={open} onOpenChange={setOpen}>
      <Drawer.Portal>
        <Drawer.Content className="fixed inset-x-0 bottom-0 z-50 mx-auto w-full max-w-md rounded-t-[24px] bg-white font-int text-ro-black shadow-sheet">
          <div className="flex w-full justify-center pb-2 pt-3">
            <div className="h-[5px] w-[42px] rounded-full bg-ro-border" />
          </div>
          <div className="px-6 pb-8">
            {step === 'phone' && (
              <>
                <h2 className="mb-2 text-lg font-extrabold">Chat with us</h2>
                <p className="mb-6 text-sm font-medium text-ro-stone">
                  Enter your phone number and we&apos;ll send you a
                  verification code.
                </p>
                <div className="mb-4 flex gap-2">
                  <select
                    aria-label="Country code"
                    value={countryCode}
                    onChange={(e) => setCountryCode(e.target.value)}
                    className="h-12 rounded-xl border border-ro-border bg-white px-3 text-sm font-semibold"
                  >
                    {COUNTRIES.map((c) => (
                      <option key={c.code} value={c.code}>
                        {c.label}
                      </option>
                    ))}
                  </select>
                  <input
                    aria-label="Phone number"
                    type="tel"
                    inputMode="numeric"
                    autoComplete="tel-national"
                    value={nationalNumber}
                    onChange={(e) => setNationalNumber(e.target.value)}
                    placeholder="9123 4567"
                    className="h-12 min-w-0 flex-1 rounded-xl border border-ro-border px-3 text-sm font-semibold"
                  />
                </div>
                {error && (
                  <p className="mb-4 text-sm font-semibold text-ro-red">
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  onClick={handlePhoneSubmit}
                  disabled={isLoading}
                  className="flex h-12 w-full items-center justify-center rounded-xl bg-ro-yellow text-sm font-extrabold text-ro-black transition-colors hover:brightness-95 disabled:opacity-50"
                >
                  <span>{isLoading ? 'Sending...' : 'Send code'}</span>
                </button>
              </>
            )}

            {step === 'otp' && (
              <>
                <h2 className="mb-2 text-lg font-extrabold">
                  Enter your code
                </h2>
                <p className="mb-6 text-sm font-medium text-ro-stone">
                  We sent a {OTP_LENGTH}-digit code to {mobile}. It expires in
                  10 minutes.
                </p>
                <input
                  aria-label="Verification code"
                  type="text"
                  inputMode="numeric"
                  autoComplete="one-time-code"
                  maxLength={OTP_LENGTH}
                  disabled={isLoading}
                  value={otp}
                  onChange={(e) => handleOtpChange(e.target.value)}
                  placeholder="0000"
                  className="mb-4 h-14 w-full rounded-xl border border-ro-border text-center text-2xl font-extrabold tracking-[0.5em] disabled:opacity-50"
                />
                {error && (
                  <p className="mb-4 text-sm font-semibold text-ro-red">
                    {error}
                  </p>
                )}
                <div className="flex items-center justify-between">
                  <button
                    type="button"
                    onClick={onBack}
                    className="text-sm font-semibold text-ro-stone underline"
                  >
                    Change number
                  </button>
                  <button
                    type="button"
                    onClick={handleResend}
                    disabled={cooldown > 0 || isLoading}
                    className="text-sm font-bold text-ro-black disabled:text-ro-stone disabled:opacity-60"
                  >
                    {cooldown > 0 ? `Resend in ${cooldown}s` : 'Resend code'}
                  </button>
                </div>
              </>
            )}
          </div>
        </Drawer.Content>
        <Drawer.Overlay className="fixed inset-0 z-40 bg-black/40" />
      </Drawer.Portal>
    </Drawer.Root>
  );
};

export default memo(LoginDrawer);
